/**
 * Dilution with units: C₁V₁ = C₂V₂ for any one unknown, plus the numbers a bench worker
 * needs next — the dilution factor and how much diluent to add.
 *
 * Concentrations are brought to the unit of C₁ and volumes to the unit of V₂ before the
 * equation is applied; the answer is converted back to the unit its own menu selected.
 * Concentrations of different kinds (mol/L against g/L, say) cannot be mixed: converting
 * between them needs a molar mass, and C₁V₁ = C₂V₂ should not guess one.
 */

import {
  type DilutionTerm,
  solveDilution,
} from "@/lib/tool-kit/chemistry/solutions";
import {
  convert,
  convertConcentration,
  findConcentrationUnit,
  VOLUME,
} from "@/lib/tool-kit/chemistry/units";

export interface DilutionInput {
  unknown: DilutionTerm;
  /** Typed values; null when blank or unreadable. The unknown's value is ignored. */
  c1: number | null;
  v1: number | null;
  c2: number | null;
  v2: number | null;
  c1Unit: string;
  c2Unit: string;
  v1Unit: string;
  v2Unit: string;
}

export type DilutionOutcome =
  | { ok: false; reason: "missing" | "invalid" | "unitMismatch" }
  | {
      ok: true;
      /** The unit all concentrations below are expressed in (C₁'s). */
      cUnit: string;
      /** The unit all volumes below are expressed in (V₂'s). */
      vUnit: string;
      c1: number;
      c2: number;
      v1: number;
      v2: number;
      /** The unknown, in the unit its own menu chose. */
      answer: number;
      answerUnit: string;
      /** C₁ ÷ C₂ */
      dilutionFactor: number;
      /** V₂ − V₁, in `vUnit`. Negative when V₁ exceeds V₂. */
      diluent: number;
      /** The final concentration is higher than the stock's. */
      concentrates: boolean;
    };

export function dilute(input: DilutionInput): DilutionOutcome {
  const { unknown } = input;
  const typed: Record<DilutionTerm, number | null> = {
    c1: input.c1,
    v1: input.v1,
    c2: input.c2,
    v2: input.v2,
  };
  const needed = (["c1", "v1", "c2", "v2"] as const).filter(
    (term) => term !== unknown
  );
  if (needed.some((term) => typed[term] === null)) {
    return { ok: false, reason: "missing" };
  }
  if (needed.some((term) => !((typed[term] ?? 0) > 0))) {
    return { ok: false, reason: "invalid" };
  }
  const sameKind =
    findConcentrationUnit(input.c1Unit).kind ===
    findConcentrationUnit(input.c2Unit).kind;
  if (!sameKind) {
    return { ok: false, reason: "unitMismatch" };
  }
  const known = {
    c1: typed.c1 ?? undefined,
    c2:
      typed.c2 === null
        ? undefined
        : (convertConcentration(typed.c2, input.c2Unit, input.c1Unit) ??
          undefined),
    v1:
      typed.v1 === null
        ? undefined
        : convert(typed.v1, VOLUME, input.v1Unit, input.v2Unit),
    v2: typed.v2 ?? undefined,
  };
  const value = solveDilution(unknown, known);
  if (value === null) {
    return { ok: false, reason: "invalid" };
  }
  const full = { ...known, [unknown]: value } as Record<DilutionTerm, number>;
  let answer = value;
  let answerUnit = input.c1Unit;
  if (unknown === "c2") {
    answer = convertConcentration(value, input.c1Unit, input.c2Unit) ?? value;
    answerUnit = input.c2Unit;
  } else if (unknown === "v1") {
    answer = convert(value, VOLUME, input.v2Unit, input.v1Unit);
    answerUnit = input.v1Unit;
  } else if (unknown === "v2") {
    answerUnit = input.v2Unit;
  }
  return {
    ok: true,
    cUnit: input.c1Unit,
    vUnit: input.v2Unit,
    c1: full.c1,
    c2: full.c2,
    v1: full.v1,
    v2: full.v2,
    answer,
    answerUnit,
    dilutionFactor: full.c1 / full.c2,
    diluent: full.v2 - full.v1,
    concentrates: full.c2 > full.c1,
  };
}
